import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { FC, useEffect, useState } from 'react';
import { useDebounceCallback } from '../../../../../hooks/useDebounceValue';
import { Input } from '../../../../components-v2/Input';
import { Label } from '../../../../components-v2/Label';
import { useLocationFilter } from '../hooks/useLocationFilter';
import { styleSheet } from './styles';

interface LocationFiltersProps {
	className?: string;
	styles?: (StyleDeclarationValue | false)[];
	filter: Api.IBulkContactsRequest;
	onChangeFilter: (filter: Api.IBulkContactsRequest) => void;
}

const DEBOUNCE_MS = 300;

export const LocationFilters: FC<LocationFiltersProps> = ({ className, styles = [], filter, onChangeFilter }) => {
	const { city, state, zip, setCity, setState, setZip } = useLocationFilter({
		filter,
		onChangeFilter,
	});

	// Local state for immediate input feedback
	const [localCity, setLocalCity] = useState(city);
	const [localState, setLocalState] = useState(state);
	const [localZip, setLocalZip] = useState(zip);

	// Sync local state when filter changes externally (e.g., loading saved search)
	useEffect(() => {
		setLocalCity(city);
	}, [city]);

	useEffect(() => {
		setLocalState(state);
	}, [state]);

	useEffect(() => {
		setLocalZip(zip);
	}, [zip]);

	// Debounced callbacks to avoid excessive API calls while typing
	const debouncedSetCity = useDebounceCallback((value: string) => setCity(value), DEBOUNCE_MS);
	const debouncedSetState = useDebounceCallback((value: string) => setState(value), DEBOUNCE_MS);
	const debouncedSetZip = useDebounceCallback((value: string) => setZip(value), DEBOUNCE_MS);

	const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setLocalCity(value);
		debouncedSetCity(value);
	};

	const handleStateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setLocalState(value);
		debouncedSetState(value);
	};

	const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setLocalZip(value);
		debouncedSetZip(value);
	};

	return (
		<div className={`${css(styleSheet.container, ...(styles?.filter(Boolean) || []))} ${className || ''}`}>
			<div className={css(styleSheet.fieldContainer)}>
				<Label htmlFor='location-city' styles={[styleSheet.fieldLabel]}>
					City
				</Label>
				<Input
					id='location-city'
					type='text'
					value={localCity}
					onChange={handleCityChange}
					placeholder='e.g., Boston'
					autoComplete='off'
				/>
			</div>
			<div className={css(styleSheet.fieldContainer)}>
				<Label htmlFor='location-state' styles={[styleSheet.fieldLabel]}>
					State/Province
				</Label>
				<Input
					id='location-state'
					type='text'
					value={localState}
					onChange={handleStateChange}
					placeholder='e.g., MA'
					autoComplete='off'
				/>
			</div>
			<div className={css(styleSheet.fieldContainer)}>
				<Label htmlFor='location-zip' styles={[styleSheet.fieldLabel]}>
					Zip/Postal Code
				</Label>
				<Input
					id='location-zip'
					type='text'
					value={localZip}
					onChange={handleZipChange}
					placeholder='e.g., 02101'
					autoComplete='off'
				/>
			</div>
		</div>
	);
};
